import { useState } from 'react';
import { FileText, Loader2, Scissors } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { form_error_message } from '@/lib/forms';
import { use_vendor_bookings } from '@/features/bookings/hooks';
import type { Booking } from '@/features/bookings/types';
import { GroomReportModal } from '@/components/groom-report-modal';
import { slot_label } from '@/lib/datetime';

const STATUS_STYLE: Record<string, string> = {
  IN_PROGRESS: 'bg-primary/12 text-primary',
  COMPLETED: 'bg-success/12 text-success',
};

function format_slot(iso: string): string {
  return slot_label(iso);
}

/** Grooming appointments a report can be authored for — session started or finished. */
function is_reportable(b: Booking): boolean {
  return b.module_type === 'GROOMER' && (b.status === 'IN_PROGRESS' || b.status === 'COMPLETED');
}

export function GroomReportsPage() {
  const bookings_q = use_vendor_bookings();
  const [report_booking, set_report_booking] = useState<Booking | null>(null);

  const bookings = (bookings_q.data ?? []).filter(is_reportable);

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Groom reports"
        description="Write up before/after results for each appointment once it's underway."
      />

      {bookings_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : bookings_q.error ? (
        <ErrorState
          data-testid="groom-reports-error"
          title="Failed to load bookings"
          description={form_error_message(bookings_q.error, 'Please try again.')}
        />
      ) : bookings.length === 0 ? (
        <EmptyState
          data-testid="groom-reports-empty"
          icon={Scissors}
          title="No reportable appointments yet"
          description="Appointments show up here once a session is in progress or completed."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border" data-testid="groom-reports-list">
              {bookings.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`groom-report-row-${b.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{format_slot(b.slot_start)}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[b.status] ?? 'bg-muted text-muted-foreground'}`}>
                        {b.status.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    </div>
                    {b.notes && <p className="truncate text-xs text-muted-foreground">“{b.notes}”</p>}
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-semibold">₹{b.price_inr}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => set_report_booking(b)} data-testid={`open-groom-report-${b.id}`}>
                    <FileText className="h-4 w-4" /> Report
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {report_booking && <GroomReportModal booking={report_booking} on_close={() => set_report_booking(null)} />}
    </div>
  );
}
